/**
 * Endpoints del módulo de Calibración (`/api/calibration_*` en EDSuite API).
 */

import { apiClient } from '@/lib/http/api-client';

import type {
  CalibracionDetalleReporte,
  CalibracionFiltros,
  CalibracionReporte,
  CalibracionStats,
  CodigoCalibracion,
  CodigoConHistorial,
  CodigoFiltros,
  DetalleReporteFiltros,
  EstadoCodigo,
  NuevoCodigoPayload,
  ReporteFiltros,
  ValidacionCodigo,
} from './calibracion.types';

export const calibracionKeys = {
  all: ['calibracion'] as const,
  codigos: (filtros: CodigoFiltros) => ['calibracion', 'codigos', filtros] as const,
  stats: ['calibracion', 'stats'] as const,
  detalleCodigo: (id: number) => ['calibracion', 'codigo', id] as const,
  filtros: ['calibracion', 'filtros'] as const,
  reporte: (filtros: ReporteFiltros) => ['calibracion', 'reporte', filtros] as const,
  detalles: (filtros: DetalleReporteFiltros) => ['calibracion', 'detalles', filtros] as const,
} as const;

/** Quita las claves vacías para no enviar filtros en blanco. */
function limpiar(filtros: Record<string, unknown>): Record<string, string> {
  const query: Record<string, string> = {};

  for (const [clave, valor] of Object.entries(filtros)) {
    if (valor === '' || valor === undefined || valor === null) continue;
    query[clave] = String(valor);
  }

  return query;
}

export const calibracionApi = {
  listarCodigos: (filtros: CodigoFiltros, signal?: AbortSignal): Promise<CodigoCalibracion[]> =>
    apiClient.get<CodigoCalibracion[]>('/api/calibration_codes', {
      query: limpiar({ estado: filtros.estado, codigo: filtros.codigo }),
      signal,
    }),

  estadisticas: (signal?: AbortSignal): Promise<CalibracionStats> =>
    apiClient.get<CalibracionStats>('/api/calibration_codes_stats', { signal }),

  detalleCodigo: (id: number, signal?: AbortSignal): Promise<CodigoConHistorial> =>
    apiClient.get<CodigoConHistorial>(`/api/calibration_codes/${id}`, { signal }),

  crearCodigo: (payload: NuevoCodigoPayload): Promise<{ message: string; codigo: CodigoCalibracion }> =>
    apiClient.post<{ message: string; codigo: CodigoCalibracion }>('/api/calibration_codes', {
      body: payload,
    }),

  cambiarEstado: (
    id: number,
    estado: EstadoCodigo,
  ): Promise<{ message: string; codigo: CodigoCalibracion }> =>
    apiClient.patch<{ message: string; codigo: CodigoCalibracion }>(`/api/calibration_codes/${id}`, {
      body: { estado },
    }),

  eliminarCodigo: (id: number, forzar = false): Promise<{ message: string }> =>
    apiClient.delete<{ message: string }>(`/api/calibration_codes/${id}`, {
      query: forzar ? { forzar: '1' } : {},
    }),

  /** Valida un código de 6 dígitos sin consumirlo. */
  validarCodigo: (codigo: string): Promise<ValidacionCodigo> =>
    apiClient.get<ValidacionCodigo>('/api/calibration_code_lookup', { query: { codigo } }),

  filtros: (signal?: AbortSignal): Promise<CalibracionFiltros> =>
    apiClient.get<CalibracionFiltros>('/api/calibration_report_filters', { signal }),

  reporte: (filtros: ReporteFiltros, signal?: AbortSignal): Promise<CalibracionReporte> =>
    apiClient.get<CalibracionReporte>('/api/calibration_report', {
      query: limpiar({ ...filtros }),
      signal,
    }),

  detalles: (filtros: DetalleReporteFiltros, signal?: AbortSignal): Promise<CalibracionDetalleReporte> =>
    apiClient.get<CalibracionDetalleReporte>('/api/calibration_report_details', {
      query: limpiar({ ...filtros }),
      signal,
    }),
} as const;
